import { useEffect } from "react";
import { Dispatch, SetStateAction } from "react";
import Drawer from "@/components/shared/drawer";
import SearchBox from "@/components/shared/searchBox";
import Badge from "@/components/shared/badge";
import Skeleton from "@/components/shared/skeleton";

type Props = {
  showDrawerOpen: boolean;
  setShowDrawerOpen: Dispatch<SetStateAction<boolean>>;
  searchKeyword: string;
  setSearchKeyword: Dispatch<SetStateAction<string>>;
  crewList?: { id: number; name: string }[];
  selectedCrewIds: number[];
  setSelectedCrewIds: Dispatch<SetStateAction<number[]>>;
  ownerFilterList: any;
  setOwnerFilterList: Dispatch<SetStateAction<any>>;
  setApplyFilter: Dispatch<SetStateAction<any>>;
};

const OwnerSelectCrewDrawer = ({
  showDrawerOpen,
  setShowDrawerOpen,
  searchKeyword,
  setSearchKeyword,
  crewList,
  selectedCrewIds,
  setSelectedCrewIds,
  ownerFilterList,
  setOwnerFilterList,
  setApplyFilter
}: Props) => {
  const handleCrewSelect = (crewId: number) => {
    const newSelectedIds = selectedCrewIds.includes(crewId)
      ? selectedCrewIds.filter((id) => id !== crewId)
      : [...selectedCrewIds, crewId].sort((a, b) => a - b);

    setSelectedCrewIds(newSelectedIds);
  };

  useEffect(() => {
    // ドロワーが閉じられた時のみ実行
    if (!showDrawerOpen) {
      const updateFilterList = () => {
        const baseFilterList = {
          ...ownerFilterList,
          page: 1
        };

        // 選択されているクルーがいる場合は crew_ids を追加
        if (selectedCrewIds.length > 0) {
          return {
            ...baseFilterList,
            crew_ids: selectedCrewIds.sort((a, b) => a - b)
          };
        }

        // 選択されているクルーがいない場合は crew_ids を削除
        const { crew_ids, ...restFilterList } = baseFilterList;
        return restFilterList;
      };

      setOwnerFilterList(updateFilterList());
      setApplyFilter("filter");
    }
  }, [showDrawerOpen]);

  // フィルターのクルーIDが空になった時に選択状態をリセット
  useEffect(() => {
    if (ownerFilterList.crew_ids?.length === 0) {
      setSelectedCrewIds([]);
    }
  }, [ownerFilterList.crew_ids, setSelectedCrewIds]);

  // 初期表示時にフィルターのクルーIDがある場合は選択状態を復元
  useEffect(() => {
    if (ownerFilterList.crew_ids?.length > 0) {
      setSelectedCrewIds(ownerFilterList.crew_ids);
    }
  }, []);
  return (
    <Drawer
      title="クルーを選択"
      isDrawerOpen={showDrawerOpen}
      setIsDrawerOpen={setShowDrawerOpen}
      height="h-[60svh]"
    >
      <div className="mx-16 mt-24 flex max-w-[55rem] flex-col gap-16">
        <SearchBox
          value={searchKeyword}
          placeholder="クルーを検索"
          setValue={setSearchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          isActivated={searchKeyword !== ""}
        />
        <div className="flex flex-wrap gap-12">
          {crewList ? (
            crewList
              .filter((crew) =>
                crew.name.toLowerCase().includes(searchKeyword.toLowerCase())
              )
              .map((crew) => (
                <Badge
                  key={crew.id}
                  variant="secondaryOutline"
                  isSelectable
                  isSelected={selectedCrewIds.includes(crew.id)}
                  onClick={() => handleCrewSelect(crew.id)}
                >
                  {crew.name}
                </Badge>
              ))
          ) : (
            // ローディング中のスケルトン表示
            <>
              <Skeleton className="h-32 w-80 rounded-md" />
              <Skeleton className="h-32 w-96 rounded-md" />
              <Skeleton className="h-32 w-72 rounded-md" />
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default OwnerSelectCrewDrawer;
